import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SeenLogo } from '@/components/common/SeenLogo';
import axios from 'axios';
import { 
  Scan, 
  ShieldCheck, 
  Leaf, 
  AlertTriangle,
  Loader2,
  Building2,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CompanyScanPage = () => {
  const { companyId } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const response = await axios.get(`${API}/company/${companyId}/public`);
        setCompany(response.data);
      } catch (err) {
        setError('Company not found');
      } finally {
        setLoading(false);
      }
    };
    fetchCompany();
  }, [companyId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#5DBDB3]/10 to-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#5DBDB3]" />
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#5DBDB3]/10 to-background">
        <div className="max-w-lg mx-auto px-4 py-12 text-center">
          <SeenLogo className="mx-auto mb-8" />
          <h1 className="text-2xl font-bold mb-4">Company Not Found</h1>
          <p className="text-muted-foreground mb-6">
            This QR code may be outdated or the company is no longer active.
          </p>
          <Link to="/">
            <Button>Go to Homepage</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#5DBDB3]/10 to-background" data-testid="company-scan-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-border sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/">
            <SeenLogo />
          </Link>
          <Link to="/auth">
            <Button variant="outline" size="sm">
              Sign In
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Company Header */}
        <Card className="mb-8 border-2 border-[#5DBDB3]/20 bg-white/50 backdrop-blur-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 rounded-xl bg-[#5DBDB3]/10 flex items-center justify-center">
                <Building2 className="w-8 h-8 text-[#5DBDB3]" />
              </div>
              <div>
                <h1 className="text-2xl font-bold" data-testid="company-name">{company.name}</h1>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className="bg-[#5DBDB3] text-white">
                    <ShieldCheck className="w-3 h-3 mr-1" />
                    Verified Partner
                  </Badge>
                  {company.subscription_plan === 'enterprise' && (
                    <Badge variant="outline" className="border-[#F08B7D] text-[#F08B7D]">
                      Enterprise
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <p className="text-muted-foreground">
              {company.name} partners with <strong>Seen Beyond the Label</strong> to provide transparent ingredient information for all their products.
            </p>
          </CardContent>
        </Card>

        {/* Action Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <Card className="border border-border hover:border-[#5DBDB3] transition-colors group">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scan className="w-5 h-5 text-[#5DBDB3]" />
                Scan a Product
              </CardTitle>
              <CardDescription>
                Scan any product barcode to see complete ingredient analysis and allergen warnings
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/auth?redirect=/scan">
                <Button className="w-full bg-[#5DBDB3] hover:bg-[#4da8a0]" data-testid="scan-product-btn">
                  Start Scanning
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card className="border border-border hover:border-[#F08B7D] transition-colors group">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#F08B7D]" />
                Set Your Allergies
              </CardTitle>
              <CardDescription>
                Create a free account to save your allergen profile and get personalized warnings
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/auth">
                <Button variant="outline" className="w-full border-[#F08B7D] text-[#F08B7D] hover:bg-[#F08B7D]/10" data-testid="create-account-btn">
                  Create Free Account
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* Features */}
        <Card className="border border-border">
          <CardHeader>
            <CardTitle>What You'll Discover</CardTitle>
            <CardDescription>
              Our AI-powered analysis reveals what labels don't tell you
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-[#5DBDB3] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Hidden Ingredients</p>
                  <p className="text-sm text-muted-foreground">Ingredients hidden behind terms like "natural flavors"</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-[#5DBDB3] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Allergen Detection</p>
                  <p className="text-sm text-muted-foreground">Identifies allergens including rare ones not in the main 14</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-[#5DBDB3] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Health Score</p>
                  <p className="text-sm text-muted-foreground">Overall product healthiness rating based on ingredients</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-[#5DBDB3] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Cross-Contamination</p>
                  <p className="text-sm text-muted-foreground">Warnings about potential trace allergens</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer CTA */}
        <div className="mt-8 text-center">
          <p className="text-sm text-muted-foreground mb-4">
            Powered by <strong>Seen Beyond the Label</strong> - Making hidden ingredients visible for everyone
          </p>
          <Link to="/about">
            <Button variant="link" className="text-[#5DBDB3]">
              Learn About Our Mission
            </Button>
          </Link>
        </div>
      </main>
    </div>
  );
};

export default CompanyScanPage;
